import { CacheConfig } from '@/shared/constants/CacheConfig';

class CacheEntry<Value> {
  constructor(
    public expireTime: number,
    public value: Value,
  ) {
  }

  isValid(now: number): boolean {
    return now < this.expireTime;
  }
}

export interface CacheOptions<LoadArgs extends Array<any>> {
  ttl: number;
  getKey: (args: LoadArgs) => string;
}

export class Cache<Value, Args extends Array<any>> {
  protected entries = new Map<string, CacheEntry<Value>>();
  protected options: CacheOptions<Args>;

  constructor(
    private load: (...args: Args) => Promise<Value>,
    options?: CacheOptions<Args>,
  ) {
    const DEFAULT_OPTIONS: CacheOptions<Array<any>> = {
      ttl: CacheConfig.DEFAULT_TTL,
      getKey: args => args.join('###'),
    };

    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  async get(...args: Args): Promise<Value> {
    const key = this.options.getKey(args);
    const now = Date.now();
    const entry = this.entries.get(key);
    if (entry?.isValid(now)) return entry.value;
    const value = await this.load(...args);
    this.entries.set(key, new CacheEntry(now + this.options.ttl, value));
    return value;
  }

  getCached(...args: Args): Value | undefined {
    const key = this.options.getKey(args);
    const entry = this.entries.get(key);
    return entry?.value;
  }

  clear(): void {
    this.entries.clear();
  }
}

export class ListCache<Value, Args extends Array<any>> extends Cache<Array<Value>, Args> {
  constructor(
    load: (...args: Args) => Promise<Array<Value>>,
    options?: CacheOptions<Args>,
  ) {
    super(load, options);
  }

  getCached(...args: Args): Array<Value> {
    return super.getCached(...args) ?? [];
  }

  /**
   * returns a concatenation of all cached values
   * BEWARE you usually want to use {@link #getCached} instead, so you can pass the correct cache key(s)!
   * NOTE in case there are no keys, {@link #getCached} is equivalent yet more efficient
   */
  getAllCached(): Array<Value> {
    return [...this.entries.values()].flatMap(e => e.value);
  }
}

export class SingletonCache<Value> extends Cache<Value, []> {
  constructor(
    load: () => Promise<Value>,
    options?: CacheOptions<[]>,
  ) {
    super(load, options);
  }
}